/**
 * Persist cart attribute fields from the cart page and cart drawer
 * (see snippets/cart-attributes.liquid) via `/cart/update.js`.
 *
 * Matches the gift-note write on the product page: save on change/blur, debounce while typing,
 * and use sendBeacon on pagehide so a checkout click does not drop the last edit.
 */
class CartAttributes extends HTMLElement {
  #lastSaved = null;

  #debounceTimer = null;

  connectedCallback() {
    this.#lastSaved = JSON.stringify(this.#payload());

    this.addEventListener('input', this.#onInput);
    this.addEventListener('change', this.#onChange);
    window.addEventListener('pagehide', this.#onPageHide);
  }

  disconnectedCallback() {
    clearTimeout(this.#debounceTimer);

    this.removeEventListener('input', this.#onInput);
    this.removeEventListener('change', this.#onChange);
    window.removeEventListener('pagehide', this.#onPageHide);
  }

  #fields() {
    return Array.from(this.querySelectorAll('[name^="attributes["]'));
  }

  #payload() {
    const attributes = {};

    for (const field of this.#fields()) {
      const match = field.getAttribute('name')?.match(/^attributes\[(.*)\]$/);
      if (match) attributes[match[1]] = field.value.trim();
    }

    return { attributes };
  }

  save(useBeacon = false) {
    clearTimeout(this.#debounceTimer);

    const payload = this.#payload();
    const serialized = JSON.stringify(payload);

    if (serialized === this.#lastSaved) return;

    this.#lastSaved = serialized;

    const url = `${window.Shopify.routes.root}cart/update.js`;

    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([serialized], { type: 'application/json' }));
      return;
    }

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: serialized,
    }).catch(() => {
      this.#lastSaved = null;
    });
  }

  #onInput = () => {
    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => this.save(), 700);
  };

  #onChange = () => this.save();

  #onPageHide = () => this.save(true);
}

if (!window.customElements.get('cart-attributes')) {
  window.customElements.define('cart-attributes', CartAttributes);
}
